import React from 'react';
import Link from '@atlaskit/link';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import type { Warning } from '@atlassian/jira-portfolio-3-portfolio/src/common/warning-details/types.tsx';
import messages from './messages.tsx';
import { WarningAction } from './WarningAction.tsx';

const TROUBLESHOOT_WARNINGS_URL =
	'https://support.atlassian.com/jira-software-cloud/docs/troubleshoot-warnings-in-review-changes-modal/';

export type Props = {
	warnings?: Warning[];
	openIssueModal: (warning: Warning) => void;
	openTransitionScreenModal: (warning: Warning) => void;
	setIsOpen: (isOpen: boolean) => void;
};

export const WarningContent = ({
	warnings,
	openIssueModal,
	openTransitionScreenModal,
	setIsOpen,
}: Props) => {
	const { formatMessage } = useIntl();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const internationaliseNestedMessages = (values: { [key: string]: any }) => {
		const amount = values.amount ? formatMessage(values.amount.key, values.amount) : null;
		return { ...values, amount };
	};

	return (
		<>
			{warnings &&
				warnings.map((warning, index) => (
					<Box key={index} xcss={entryStyles}>
						<Box xcss={headingStyles}>
							{warnings.length > 1 && `${index + 1}. `}
							<FormattedMessage
								{...warning.header}
								values={internationaliseNestedMessages(warning.messageValues)}
							/>
						</Box>
						{warning.message && (
							<FormattedMessage
								{...warning.message}
								values={internationaliseNestedMessages(warning.messageValues)}
							/>
						)}
						{!warning.isManualCreateRequired && !warning.isTransitionScreenRequired && (
							<Box paddingBlockStart="space.100">
								<Link href={TROUBLESHOOT_WARNINGS_URL} target="_blank">
									{formatMessage(messages.troubleshootingLinkLabel)}
								</Link>
							</Box>
						)}
						{warning.isManualCreateRequired && (
							<WarningAction
								warning={warning}
								action={openIssueModal}
								setIsOpen={setIsOpen}
								message={messages.openIssueModalButtonLabel}
							/>
						)}
						{warning.isTransitionScreenRequired && (
							<WarningAction
								warning={warning}
								action={openTransitionScreenModal}
								setIsOpen={setIsOpen}
								message={messages.openTransitionScreenModalButtonLabel}
							/>
						)}
					</Box>
				))}
		</>
	);
};

const entryStyles = xcss({
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	minWidth: '300px',
	maxWidth: '448px',
});

const headingStyles = xcss({
	fontWeight: token('font.weight.bold'),
	marginBottom: 'space.100',
});
